import api from '../utils/axiosConfig';
import { User } from '../types';

export interface CreateUserRequest {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role?: string;
}

export class UserService {
  /**
   * Check if a user exists by ID
   * Calls GET /users/<user_id>
   */
  static async checkUserExists(userId: string): Promise<User | null> {
    try {
      const response = await api.get(`/users/${userId}`);
      return response.data;
    } catch (error: any) {
      const status = error.response?.status;
      // Handle both standard 404 and backends that return 400 with status_code: 404
      if (status === 404 || status === 400) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Create a new user
   * Calls POST /users
   */
  static async createUser(userData: CreateUserRequest): Promise<User> {
    try {
      const response = await api.post('/users', userData);
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Ensure user exists - check if user exists, create if not
   * This is the main function to be called during authentication
   */
  static async ensureUserExists(userData: CreateUserRequest): Promise<User> {
    try {
      // First, check if user exists
      const existingUser = await this.checkUserExists(userData.id);
      
      if (existingUser) {
        return existingUser;
      }

      // If user doesn't exist, create them
      console.log('User not found, creating new user:', userData.id);
      const newUser = await this.createUser(userData);
      return newUser;
    } catch (error: any) {
      console.error('Error ensuring user exists:', error);
      
      // Provide more specific error messages
      if (error.response?.status === 404) {
        throw new Error('Failed to check user existence - backend endpoint not found');
      } else if (error.response?.status >= 500) {
        throw new Error('Backend server error - please try again later');
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        throw new Error('Unable to connect to backend server');
      } else {
        throw new Error(error.response?.data?.message || error.message || 'Failed to setup user account');
      }
    }
  }
}